import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { StatusMessage, createStatusMessage, updateStatusMessage } from '../../../API/status-message';
import { useUserData } from '../../../hooks/use-user-data';
import { useErrorHandler } from './status-message-utils';

interface StatusMessageDialogProps {
  open: boolean;
  onClose: (refreshNeeded: boolean) => void;
  message: StatusMessage | null;
  isNew: boolean;
}

const StatusMessageDialog: React.FC<StatusMessageDialogProps> = ({ open, onClose, message, isNew }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token } = useUserData();
  const { handleError } = useErrorHandler();

  // Internal form state uses textEn, textDe, isActive
  // These will be mapped to englishText, germanText, active when sending to the API
  const [textEn, setTextEn] = useState('');
  const [textDe, setTextDe] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [errors, setErrors] = useState({
    textEn: false,
    textDe: false,
  });

  // Reset form when dialog opens or message changes
  useEffect(() => {
    if (message) {
      setTextEn(message.englishText);
      setTextDe(message.germanText);
      setIsActive(message.active);
    } else {
      setTextEn('');
      setTextDe('');
      setIsActive(true);
    }
    setErrors({
      textEn: false,
      textDe: false,
    });
  }, [message, open]);

  const validateForm = () => {
    const newErrors = {
      textEn: !textEn.trim(),
      textDe: !textDe.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async () => {
    if (!token || !validateForm()) return;

    try {
      if (isNew) {
        const response = await createStatusMessage({
          textEn,
          textDe,
          isActive,
        });

        if (response.data) {
          onClose(true);
        } else if (response.error) {
          handleError(response.error, 'Failed to create status message');
        }
      } else if (message) {
        const response = await updateStatusMessage(message.id, {
          textEn,
          textDe,
          isActive,
        });

        if (response.data) {
          onClose(true);
        } else if (response.error) {
          handleError(response.error, 'Failed to update status message');
        }
      }
    } catch (error) {
      console.error('Failed to save status message', error);
      handleError(error, 'Failed to save status message');
    }
  };

  const handleTextEnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextEn(event.target.value);
  };
  const handleTextDeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextDe(event.target.value);
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{isNew ? t('addNewMessage') : t('editMessage')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <PaiTextField
            label={t('englishText')}
            placeholder={t('enterEnglishTextHere')}
            value={textEn}
            error={errors.textEn}
            errorText={t('englishTextRequired')}
            onChange={handleTextEnChange}
          />
          <PaiTextField
            label={t('germanText')}
            placeholder={t('enterGermanTextHere')}
            value={textDe}
            error={errors.textDe}
            errorText={t('germanTextRequired')}
            onChange={handleTextDeChange}
          />
          <PaiCheckbox label={t('active')} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={() => onClose(false)} />
        <PaiButton text={t('save')} size={'small'} border={'rounded'} action={handleSubmit} disabled={textEn.trim() === '' || textDe.trim() === ''} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default StatusMessageDialog;
